import { useRef, useState, useMemo, useContext } from 'react'
import React from 'react'

import {
  Box,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material'
import { IconChevronDown, IconBrain } from '@tabler/icons-react'
import type { IconProps } from '@tabler/icons-react'
import toast from 'react-hot-toast'
import type { Assistant } from 'types/graphql'

import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { CREATE_ASSISTANT_AUTHORIZATION_REQUEST } from 'src/components/Billing/mutations'
import { DayContext } from 'src/lib/dayContext'
import { logger } from 'src/lib/logger'
import {
  AVAILABLE_MODELS,
  PROVIDER_COLORS,
  PROVIDER_LOGOS,
} from 'src/lib/models'
import type { AvailableModelId } from 'src/lib/models'

import { GET_ASSISTANT_TIERS } from '../Assistant/queries'

const groupModelsByProvider = () => {
  const grouped: Record<
    string,
    {
      id: AvailableModelId
      model: (typeof AVAILABLE_MODELS)[keyof typeof AVAILABLE_MODELS]
    }[]
  > = {}
  for (const [id, model] of Object.entries(AVAILABLE_MODELS)) {
    const provider = model.provider
    if (!grouped[provider]) {
      grouped[provider] = []
    }
    grouped[provider].push({ id: id as AvailableModelId, model })
  }
  return grouped
}

interface ModelChooserProps {
  modelId: AvailableModelId | 'default'
  onModelSelect: (modelId: AvailableModelId | string) => void
  thinkingEnabled?: boolean
  assistant?: Assistant | null
  size?: 'small' | 'medium'
  className?: string
  showDefaultOption?: boolean
}

const menuSx = {
  p: 0,
  m: 0,
  '& .MuiPaper-root': {
    width: '264px',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  '& .model-chooser-menu-item': {
    width: '100%',
    py: '8px',
    px: '12px',
    transition: 'all 0.2s ease-in-out',
    '&.disabled': {
      opacity: 0.5,
      '&:hover': {
        opacity: 1,
        '& .MuiListItemText-root': {
          opacity: 0.7,
        },
      },
    },
    '& .MuiListItemIcon-root': {
      minWidth: '36px',
      pl: '4px',
    },
    '& .MuiListItemText-root': {
      '& .MuiListItemText-primary': {
        fontSize: '12px',
        fontWeight: 600,
        letterSpacing: '-0.5px',
        fontFamily: 'Poppins',
        lineHeight: '1.4',
      },
      '& .MuiListItemText-secondary': {
        fontSize: '11px',
        opacity: 0.6,
        lineHeight: '1',
      },
    },
  },
}

const ModelChooser = ({
  modelId,
  onModelSelect,
  thinkingEnabled = false,
  assistant = null,
  size = 'small',
  className = '',
  showDefaultOption = false,
}: ModelChooserProps) => {
  const { currentUser } = useAuth()
  const { selectedWorkspace } = useContext(DayContext)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const anchorEl = useRef<HTMLDivElement | null>(null)
  const groupedModels = useMemo(() => groupModelsByProvider(), [])

  const { data: tiersData } = useQuery(GET_ASSISTANT_TIERS)

  const [createAuthorizationRequest] = useMutation(
    CREATE_ASSISTANT_AUTHORIZATION_REQUEST
  )

  const handlePopoverOpen = () => {
    setPopoverOpen(true)
  }

  const handlePopoverClose = () => {
    setPopoverOpen(false)
  }

  const handleModelSelect = (selectedModelId: AvailableModelId | string) => {
    onModelSelect(selectedModelId)
    handlePopoverClose()
  }

  const handleRequestTierUpgrade = async (modelId: AvailableModelId) => {
    const tier = tiersData?.assistantTiers.find((tier) =>
      tier.models.includes(modelId)
    )

    const tierId = tier?.id
    if (!tierId || !currentUser?.id || !selectedWorkspace) {
      toast.error('Unable to request tier upgrade')
      logger.dev('Unable to request tier upgrade', {
        tierId,
        currentUser,
        selectedWorkspace,
      })
      return
    }

    toast.promise(
      createAuthorizationRequest({
        variables: {
          workspaceId: selectedWorkspace,
          userId: currentUser.id,
          tierId,
          businessCase: null,
        },
      }),
      {
        loading: 'Requesting tier upgrade...',
        success: 'Tier upgrade request submitted',
        error: 'Failed to request tier upgrade',
      }
    )
  }

  const currentModel =
    modelId === 'default' ? null : AVAILABLE_MODELS[modelId as AvailableModelId]
  const modelName =
    modelId === 'default'
      ? 'Default Model'
      : (currentModel?.name ?? 'Unknown Model')

  return (
    <Box ref={anchorEl as React.RefObject<HTMLDivElement>}>
      <Chip
        data-nocollapse="true"
        icon={thinkingEnabled ? <IconBrain size={16} /> : undefined}
        label={modelName}
        size={size}
        onClick={handlePopoverOpen}
        onDelete={handlePopoverOpen}
        deleteIcon={<IconChevronDown size={16} />}
        className={className}
      />
      <Menu
        id="model-select-popover"
        open={popoverOpen}
        sx={menuSx}
        anchorEl={anchorEl.current}
        onClose={handlePopoverClose}
      >
        {showDefaultOption && (
          <MenuItem
            value="default"
            selected={modelId === 'default'}
            onClick={() => handleModelSelect('default')}
            className="model-chooser-menu-item"
          >
            <ListItemIcon>
              <Box sx={{ width: 16 }} />
            </ListItemIcon>
            <ListItemText primary="Default Model" />
          </MenuItem>
        )}
        {Object.values(groupedModels)
          .flat()
          .map(({ id, model }) => {
            // Check if model is available in the assistant's tier
            const tierModels = assistant?.tier?.models
            const isModelAvailable =
              !tierModels || tierModels.length === 0 || tierModels.includes(id)

            return (
              <MenuItem
                key={id}
                value={id}
                selected={id === modelId}
                onClick={(e) => {
                  e.stopPropagation()
                  if (isModelAvailable) {
                    handleModelSelect(id)
                  } else {
                    handleRequestTierUpgrade(id)
                  }
                }}
                className={`model-chooser-menu-item ${
                  !isModelAvailable ? 'disabled' : ''
                }`}
              >
                <ListItemIcon>
                  {React.createElement(
                    PROVIDER_LOGOS[model.provider] as React.FC<IconProps>,
                    {
                      size: 16,
                      style: {
                        color:
                          PROVIDER_COLORS[model.provider] ?? 'primary.main',
                      },
                    }
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={model.name}
                  secondary={model.description}
                />
                {!isModelAvailable && (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRequestTierUpgrade(id)
                    }}
                  >
                    Upgrade
                  </Button>
                )}
              </MenuItem>
            )
          })}
      </Menu>
    </Box>
  )
}

export default ModelChooser
